import { timingSafeEqual } from 'crypto';

/**
 * Known LOCI services that participate in service-to-service auth.
 * Extend this union as new services are onboarded. Server-side services
 * mirror the `SERVICE_ENV_SCHEMAS` keys in `@loci/config`; `web` is the
 * Next.js app's server runtime (SSR fetchers only, API-1768), which has its
 * own env schema in `apps/web/config/env.ts`.
 */
export type ServiceName =
  | 'backend-api'
  | 'geo-core'
  | 'affiliate-checkout'
  | 'data-pipeline'
  | 'graphql-api'
  | 'ws-subscription-server'
  | 'web';

/** Maps a service name to its API key and the set of target services it may call. */
export interface ServiceKeyEntry {
  key: string;
  allowedTargets: ReadonlySet<ServiceName>;
}

/** Full registry mapping service names to their credentials and audience rules. */
export type ServiceKeyRegistry = Partial<Record<ServiceName, ServiceKeyEntry>>;

/** Configuration passed to {@link validateServiceAuth}. */
export interface ServiceAuthConfig {
  /** Per-service key registry (from env or config). */
  registry: ServiceKeyRegistry;
}

/** Successful validation result. */
export interface ServiceAuthSuccess {
  valid: true;
  /** The declared service name from the `X-Service-Name` header. */
  serviceName: ServiceName;
}

/** Failed validation result. */
export interface ServiceAuthFailure {
  valid: false;
  reason: 'missing_headers' | 'unknown_service' | 'invalid_key' | 'audience_denied';
}

export type ServiceAuthResult = ServiceAuthSuccess | ServiceAuthFailure;

/** Minimal request-like object — works with Express, Koa, or plain objects in tests. */
export interface ServiceAuthRequest {
  headers: Record<string, string | string[] | undefined>;
}

function getHeader(req: ServiceAuthRequest, name: string): string | undefined {
  const raw = req.headers[name];
  if (Array.isArray(raw)) return raw[0];
  return raw;
}

/**
 * Compare two strings in constant time to prevent timing attacks.
 * Returns `false` for empty or length-mismatched inputs.
 */
function timingSafeCompare(a: string, b: string): boolean {
  if (a.length === 0 || b.length === 0) return false;
  if (a.length !== b.length) return false;
  return timingSafeEqual(Buffer.from(a, 'utf-8'), Buffer.from(b, 'utf-8'));
}

/**
 * Validates a service-to-service request.
 *
 * Reads `x-service-name` and `x-api-key` headers, looks up the caller in the
 * provided registry, performs a timing-safe key comparison, and enforces
 * audience scoping (the caller must be authorized to reach `targetService`).
 *
 * @param req           Incoming request (or request-like object).
 * @param targetService The service that is being called.
 * @param config        Per-service key registry.
 */
export function validateServiceAuth(
  req: ServiceAuthRequest,
  targetService: ServiceName,
  config: ServiceAuthConfig,
): ServiceAuthResult {
  const serviceName = getHeader(req, 'x-service-name') as ServiceName | undefined;
  const apiKey = getHeader(req, 'x-api-key');

  if (!serviceName || !apiKey) {
    return { valid: false, reason: 'missing_headers' };
  }

  // Own-property lookup only (API-1768): `registry` is a plain object, so a
  // bare `registry[name]` resolves `Object.prototype` members for a crafted
  // `x-service-name` such as `__proto__` or `constructor`, whose missing `key`
  // then threw inside the compare. This predicate runs on every request once
  // the registry is non-empty, so it must reject, never throw.
  const entry = Object.prototype.hasOwnProperty.call(config.registry, serviceName)
    ? config.registry[serviceName]
    : undefined;
  if (!entry || typeof entry.key !== 'string') {
    return { valid: false, reason: 'unknown_service' };
  }

  if (!timingSafeCompare(apiKey, entry.key)) {
    return { valid: false, reason: 'invalid_key' };
  }

  if (!entry.allowedTargets.has(targetService)) {
    return { valid: false, reason: 'audience_denied' };
  }

  return { valid: true, serviceName };
}
